import { DirectionsRenderer, GoogleMap, useJsApiLoader } from '@react-google-maps/api'
import React, { ReactElement, useCallback, useEffect, useState } from 'react'
import { usePrevious } from 'react-use'

import { t } from '../../../utils/translation-utils'
import Alert from '../../elements/alert'
import Switch from '../../elements/switch'
import LoadingOverlay from '../../widgets/LoadingOverlay'
type DirectionsResult = google.maps.DirectionsResult

const getGoogleMapsKey = () => {
  return process.env.REACT_APP_GOOGLE_MAPS_KEY || 'AIzaSyDQRws1zrCcwmbyxagxaavAmQ9BwBtcYME'
}

type Props = {
  from?: string
  to?: string
  waypoints?: string[]
  avoidFerries?: boolean
  updateDistance: (leg: number, meters: number, avoidFerries: boolean) => void
  updateReturnDistance?: (leg: number, meters: number) => void
}

export const MapCentre = { lat: 56.2, lng: 10.5 }

export default function CarAllowanceMap(props: Props): ReactElement | null {
  type State = {
    directions: DirectionsResult | null
    warning?: string
    avoidFerries: boolean
  }
  const [state, setState] = useState<State>({ directions: null, avoidFerries: props.avoidFerries || false })
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: getGoogleMapsKey(),
    libraries: ['places'],
  })

  const { updateDistance, updateReturnDistance } = props

  const updateDirections = useCallback(
    (from: string, to: string, waypoints: string[], avoidFerries: boolean) => {
      const interval = setInterval(() => {
        // Ensure Maps have been loaded
        if (!window.google) {
          return
        }
        clearInterval(interval)
        waypoints = waypoints.filter((w) => !!w)

        const DirectionsService = new window.google.maps.DirectionsService()
        DirectionsService.route(
          {
            origin: from,
            destination: to,
            waypoints: waypoints.map((w) => ({ location: w, stopover: true })),
            travelMode: window.google.maps.TravelMode.DRIVING,
            avoidFerries: avoidFerries,
          },
          (result, status) => {
            if (status === window.google.maps.DirectionsStatus.OK) {
              setState((prev) => ({ ...prev, directions: result }))
              if (result && result.routes.length > 0) {
                const legs = result.routes[0].legs
                legs.forEach((leg, i) => {
                  if (leg.distance) {
                    updateDistance(i, leg.distance.value, state.avoidFerries)
                  }
                })
              }
            } else {
              setState((prev) => ({ ...prev, warning: t('car_allowances_tab.map.no_route') }))
            }
          }
        )
        if (updateReturnDistance) {
          DirectionsService.route(
            {
              origin: to,
              destination: from,
              waypoints: waypoints.reverse().map((w) => ({ location: w, stopover: true })),
              travelMode: window.google.maps.TravelMode.DRIVING,
              avoidFerries: avoidFerries,
            },
            (result, status) => {
              if (status === window.google.maps.DirectionsStatus.OK) {
                if (result && result.routes.length > 0) {
                  const legs = result.routes[0].legs
                  legs.forEach((leg, i) => {
                    if (leg.distance) {
                      if (updateReturnDistance) {
                        updateReturnDistance(i, leg.distance.value)
                      }
                    }
                  })
                }
              } else {
                setState((prev) => ({ ...prev, warning: t('car_allowances_tab.map.no_return_route') }))
              }
            }
          )
        }
      }, 10)
    },
    [updateDistance, updateReturnDistance, state]
  )

  const { from, to, waypoints } = props
  const previousFrom = usePrevious(from)
  const previousTo = usePrevious(to)
  const previousWaypoints = usePrevious(waypoints)
  const previousState = usePrevious(state)

  useEffect(() => {
    if (from && to) {
      if (
        from === previousFrom &&
        to === previousTo &&
        waypoints?.toString() === previousWaypoints?.toString() &&
        (!previousState || previousState.avoidFerries === state.avoidFerries)
      ) {
        return // ignore duplicate calls
      }
      setState((prev) => ({ ...prev, warning: undefined }))
      updateDirections(from, to, waypoints ?? [], state.avoidFerries)
    } else if (!!state.directions && !!state.warning) {
      setState((prev) => ({ ...prev, directions: null, warning: undefined }))
    }
  }, [from, to, waypoints, previousFrom, previousTo, previousWaypoints, state, previousState, updateDirections])

  const _toggleFerries = (ferry: boolean) => {
    setState((prev) => ({ ...prev, avoidFerries: ferry }))
    if (from && to) {
      updateDirections(from, to, waypoints ?? [], ferry)
    }
  }

  if (!isLoaded) {
    return <LoadingOverlay />
  }

  return (
    <div>
      <GoogleMap
        zoom={6}
        center={MapCentre}
        options={{ disableDefaultUI: true }}
        mapContainerClassName="employees-car-allowance-map"
        id="employees-car-allowance-map"
      >
        {state.directions && <DirectionsRenderer directions={state.directions} />}
      </GoogleMap>
      <div>
        <div className="ant-switch-wrapper">
          <Switch onChange={_toggleFerries} checked={state.avoidFerries} />
          <span className="ant-switch-text">{t('car_allowances_tab.map.avoid_ferries')}</span>
        </div>
      </div>
      {state.warning && (
        <Alert message={state.warning} type="warning" showIcon className="employees-car-allowance-warning" />
      )}
    </div>
  )
}
